import { observable, action } from "mobx";
import makeApi, { URLS } from "Utils/apiURL";
import { toasterState } from "Components/common/toaster";
import { RATE_WIDGET_IDS, CATEGORY_ENDPOINT_MAP } from "../utils/widgetConstants";
import { resolveDatePreset } from "../utils/datePresetUtils";
import { resolveChartData } from "../utils/chartDataUtils";

const LATEST_REPLIES_WIDGET_ID = "LATEST_REPLIES";

export const DASHBOARD_DATE_FILTER_KEY = "dashboard-date-filter";

class WidgetDataStore {
  @observable widgetData = {};
  @observable loadingByCategory = {};
  @observable activeDateFilter = "7d";
  @observable activeCustomRange = null;
  @observable activeMemberIds = [];

  persistDateFilter = () => {
    try {
      localStorage.setItem(
        DASHBOARD_DATE_FILTER_KEY,
        JSON.stringify({ filter: this.activeDateFilter, customRange: this.activeCustomRange })
      );
    } catch (e) {}
  };

  @action
  restorePersistedDateFilter = () => {
    try {
      const raw = localStorage.getItem(DASHBOARD_DATE_FILTER_KEY);
      if (raw == null) return;
      const saved = JSON.parse(raw);
      if (!saved || !saved.filter) return;
      this.activeDateFilter = saved.filter;
      this.activeCustomRange =
        saved.filter === "custom" && saved.customRange ? saved.customRange : null;
    } catch (e) {
      localStorage.removeItem(DASHBOARD_DATE_FILTER_KEY);
    }
  };

  @action
  setDateFilter = preset => {
    this.activeDateFilter = preset;
    this.persistDateFilter();
  };

  @action
  setCustomDateRange = range => {
    if (!range || range.fromDate == null || range.toDate == null) return;
    this.activeCustomRange = { fromDate: range.fromDate, toDate: range.toDate };
    this.activeDateFilter = "custom";
    this.persistDateFilter();
  };

  resolveActiveRange = () => {
    if (this.activeDateFilter === "custom" && this.activeCustomRange) {
      return { fromDate: this.activeCustomRange.fromDate, toDate: this.activeCustomRange.toDate };
    }
    return resolveDatePreset(this.activeDateFilter);
  };

  @action
  setMemberIds = ids => {
    this.activeMemberIds = ids || [];
  };

  @action
  setLoadingForCategory = (category, value) => {
    this.loadingByCategory = { ...this.loadingByCategory, [category]: value };
  };

  @action
  setWidgetData = (widgetId, data) => {
    this.widgetData = { ...this.widgetData, [widgetId]: data };
  };

  getWidgetData = widgetId => {
    return this.widgetData[widgetId] || null;
  };

  isCategoryLoading = category => {
    return !!this.loadingByCategory[category];
  };

  @action
  clearWidgetData = widgetId => {
    const updated = { ...this.widgetData };
    delete updated[widgetId];
    this.widgetData = updated;
  };

  fetchSingleWidget = async (widget, dashboardId) => {
    if (!widget?.widgetId) return;
    const { fromDate, toDate } = this.resolveActiveRange();
    const memberIds = this.activeMemberIds;
    if (widget.widgetId === LATEST_REPLIES_WIDGET_ID) {
      this.setLoadingForCategory(LATEST_REPLIES_WIDGET_ID, true);
      await this.fetchLatestReplies(fromDate, toDate, memberIds, dashboardId, !!widget.memberBreakdown);
    } else if (RATE_WIDGET_IDS.has(widget.widgetId)) {
      this.setLoadingForCategory(widget.widgetId, true);
      await this.fetchRateSeriesData(widget.widgetId, fromDate, toDate, memberIds, dashboardId, !!widget.memberBreakdown);
    } else {
      this.setLoadingForCategory(widget.category, true);
      await this.fetchCategoryData(
        widget.category, [widget.widgetId], fromDate, toDate, memberIds, dashboardId, !!widget.memberBreakdown
      );
    }
  };

  fetchWidgetMemberBreakdown = async (widget, dashboardId) => {
    if (!widget?.widgetId) return;
    const { fromDate, toDate } = this.resolveActiveRange();
    if (widget.widgetId === LATEST_REPLIES_WIDGET_ID) {
      await this.fetchLatestReplies(fromDate, toDate, this.activeMemberIds, dashboardId, true);
      return;
    }
    if (RATE_WIDGET_IDS.has(widget.widgetId)) {
      await this.fetchRateSeriesData(widget.widgetId, fromDate, toDate, this.activeMemberIds, dashboardId, true);
      return;
    }
    await this.fetchCategoryData(widget.category, [widget.widgetId], fromDate, toDate, this.activeMemberIds, dashboardId, true);
  };

  @action
  fetchAllWidgetData = async (widgets, dashboardId) => {
    if (!widgets?.length) return;

    const { fromDate, toDate } = this.resolveActiveRange();
    const memberIds = this.activeMemberIds;

    const categoryMap = {};
    const categoryBreakdownMap = {};
    const rateWidgets = [];
    let hasLatestRepliesWidget = false;

    widgets.forEach(w => {
      if (w.widgetId === LATEST_REPLIES_WIDGET_ID) {
        hasLatestRepliesWidget = true;
        this.setLoadingForCategory(LATEST_REPLIES_WIDGET_ID, true);
      } else if (RATE_WIDGET_IDS.has(w.widgetId)) {
        rateWidgets.push(w);
        this.setLoadingForCategory(w.widgetId, true);
      } else {
        const target = w.memberBreakdown ? categoryBreakdownMap : categoryMap;
        if (!target[w.category]) target[w.category] = [];
        target[w.category].push(w.widgetId);
      }
    });

    Object.keys(categoryMap).forEach(cat => this.setLoadingForCategory(cat, true));
    Object.keys(categoryBreakdownMap).forEach(cat => this.setLoadingForCategory(cat, true));

    const categoryFetches = [
      ...Object.entries(categoryMap).map(([category, widgetIds]) =>
        this.fetchCategoryData(category, widgetIds, fromDate, toDate, memberIds, dashboardId, false)
      ),
      ...Object.entries(categoryBreakdownMap).map(([category, widgetIds]) =>
        this.fetchCategoryData(category, widgetIds, fromDate, toDate, memberIds, dashboardId, true)
      )
    ];

    const rateFetches = rateWidgets.map(w =>
      this.fetchRateSeriesData(w.widgetId, fromDate, toDate, memberIds, dashboardId, !!w.memberBreakdown)
    );

    const latestRepliesWidget = widgets.find(w => w.widgetId === LATEST_REPLIES_WIDGET_ID);
    const latestRepliesFetch = hasLatestRepliesWidget
      ? [this.fetchLatestReplies(fromDate, toDate, memberIds, dashboardId, !!latestRepliesWidget?.memberBreakdown)]
      : [];

    await Promise.all([...categoryFetches, ...rateFetches, ...latestRepliesFetch]);
  };

  fetchCategoryData = async (category, widgetIds, fromDate, toDate, memberIds, dashboardId, memberBreakdown = false) => {
    const urlKey = CATEGORY_ENDPOINT_MAP[category];
    if (!urlKey || !URLS[urlKey]) return;

    try {
      const res = await makeApi({
        url: URLS[urlKey],
        method: "POST",
        data: { widgetIds, fromDate, toDate, memberIds, dashboardId, memberBreakdown }
      });
      const data = res?.data;
      if (data?.status === "failed") {
        console.error(`DashboardV2: fetchCategoryData(${category}) failed`, data?.error);
        toasterState.setToastMsg(`Failed to load ${category.toLowerCase()} widget data`, "fail");
        return;
      }
      if (data) {
        widgetIds.forEach(widgetId => {
          this.setWidgetData(widgetId, { ...data, _category: category });
        });
      }
    } catch (err) {
      console.error(`DashboardV2: fetchCategoryData(${category}) error`, err);
      toasterState.setToastMsg(`Failed to load ${category.toLowerCase()} widget data`, "fail");
    } finally {
      this.setLoadingForCategory(category, false);
    }
  };

  resolveScopeMemberIds = async (widgetId, memberIds, dashboardId) => {
    if (!dashboardId) return memberIds || [];
    try {
      const res = await makeApi({
        url: URLS.resolveWidgetScope,
        method: "POST",
        data: { widgetId, dashboardId, memberIds: memberIds || [] }
      });
      const data = res?.data;
      if (data?.status === "success" && Array.isArray(data.memberIds)) {
        return data.memberIds;
      }
    } catch (err) {
      console.error("DashboardV2: resolveScopeMemberIds error", err);
    }
    return memberIds || [];
  };

  fetchLatestReplies = async (fromDate, toDate, memberIds, dashboardId, memberBreakdown = false) => {
    try {
      const scopedMemberIds = await this.resolveScopeMemberIds(LATEST_REPLIES_WIDGET_ID, memberIds, dashboardId);
      const [feedRes, latestRepliesByMember] = await Promise.all([
        makeApi({
          url: URLS.outbox,
          method: "POST",
          data: {
            pageNo: 1,
            limit: 5,
            sortDirection: "DESC",
            sortParam: "REPLY_DATE",
            filters: {
              memberIds: scopedMemberIds || [],
              filterKey: "REPLY_COUNT",
              channel: ["EMAIL", "LINKEDIN"],
              replied: { fromDate, toDate },
              statuses: [],
              all: false
            }
          }
        }),
        memberBreakdown
          ? this.fetchLatestRepliesBreakdown(memberIds, dashboardId, fromDate, toDate)
          : Promise.resolve([])
      ]);

      const data = feedRes?.data;
      this.setWidgetData(LATEST_REPLIES_WIDGET_ID, { replies: data?.result || [], latestRepliesByMember });
    } catch (err) {
      console.error("DashboardV2: fetchLatestReplies error", err);
      this.setWidgetData(LATEST_REPLIES_WIDGET_ID, { replies: [], latestRepliesByMember: [] });
    } finally {
      this.setLoadingForCategory(LATEST_REPLIES_WIDGET_ID, false);
    }
  };

  fetchLatestRepliesBreakdown = async (memberIds, dashboardId, fromDate, toDate) => {
    try {
      const res = await makeApi({
        url: URLS.getDashboardWidgetsLatestRepliesBreakdown,
        method: "POST",
        data: { widgetIds: [LATEST_REPLIES_WIDGET_ID], memberIds: memberIds || [], dashboardId, fromDate, toDate, memberBreakdown: true }
      });
      const data = res?.data;
      if (data?.status === "success" && Array.isArray(data.latestRepliesByMember)) {
        return data.latestRepliesByMember;
      }
    } catch (err) {
      console.error("DashboardV2: fetchLatestRepliesBreakdown error", err);
    }
    return [];
  };

  fetchRateSeriesData = async (widgetId, fromDate, toDate, memberIds, dashboardId, memberBreakdown = false) => {
    try {
      const res = await makeApi({
        url: URLS.getDashboardWidgetsRateSeries,
        method: "POST",
        data: { widgetId, fromDate, toDate, memberIds, dashboardId, memberBreakdown }
      });
      const data = res?.data;
      if (data?.status === "failed") {
        console.error(`DashboardV2: fetchRateSeriesData(${widgetId}) failed`, data?.error);
        toasterState.setToastMsg(data?.error || "Failed to load rate widget data", "fail");
        return;
      }
      if (data) {
        const chartData = resolveChartData(data, this.activeDateFilter, this.activeCustomRange);
        this.setWidgetData(widgetId, {
          currentRate: data.currentRate,
          previousRate: data.previousRate,
          trend: data.trend,
          chartData,
          series: data.series || [],
          rateByMember: data.rateByMember || [],
          _isRate: true
        });
      }
    } catch (err) {
      console.error(`DashboardV2: fetchRateSeriesData(${widgetId}) error`, err);
      toasterState.setToastMsg("Failed to load rate widget data", "fail");
    } finally {
      this.setLoadingForCategory(widgetId, false);
    }
  };
}

const widgetDataStore = new WidgetDataStore();
widgetDataStore.restorePersistedDateFilter();
export { widgetDataStore };
export default widgetDataStore;
